import { ReactNode, useEffect, useRef } from 'react'
import { Money } from 'lib/money'
import { formatPercentage } from 'lib/percentage'
import { useCalculatorResult } from 'containers/calculator/hooks/useCalculatorResult'
import style from 'containers/calculator/styles/components/calculatorResult.module.css'
import flairStyle from 'containers/calculator/styles/components/flair.module.css'

const Flair = ({ children }: { children: ReactNode }) => (
    <div className={flairStyle['flair']}>{children}</div>
)

const MonthlyPayment = () => {
    const { discounted } = useCalculatorResult()

    const monthlyRepayment = discounted.totalAmount / discounted.term
    const ref = useRef(monthlyRepayment)

    useEffect(() => {
        const element = document.querySelector('#monthly-amount')

        let animationFrame: any
        if (!element) {
            return
        }
        // calculate increment value
        const step = Math.floor((monthlyRepayment - ref.current) / 25)

        const animate = () => {
            const currentValue = ref.current
            const newValue = monthlyRepayment

            // the next value after the increment
            const calculatedValue = currentValue + step

            // update value to the next value when the difference is greater than the step
            if (Math.abs(calculatedValue - newValue) > Math.abs(step)) {
                element.innerHTML = Money.format(calculatedValue, {
                    minimumFractionDigits: 0,
                })
                ref.current = calculatedValue

                animationFrame = requestAnimationFrame(() => animate())
            } else {
                element.innerHTML = Money.format(newValue, { minimumFractionDigits: 0 })
                ref.current = newValue
            }
        }

        animate()
        return () => {
            cancelAnimationFrame(animationFrame)
        }
    }, [monthlyRepayment])

    return (
        <>
            <p id="monthly-amount">{Money.format(monthlyRepayment)}</p>
        </>
    )
}

export const CalculatorResult = () => {
    const { standard, discounted } = useCalculatorResult()

    return (
        <div className={style['calculator-result']}>
            <div className={style['monthly-repayment']}>
                <Flair>monthly repayments</Flair>
                <MonthlyPayment />
            </div>
            <div className={style['flex-space-between']}>
                <p>Fee</p>
                <p style={{ fontWeight: 500 }}>
                    {Money.format(discounted.fixedFeeAmount)} (
                    {formatPercentage(discounted.fixedFeePercentage, {
                        representedAs: 'fraction',
                    })}
                    )
                </p>
            </div>
            <div className={style['flex-space-between']}>
                <p>Discount</p>
                <p>
                    -{Money.format(standard.totalAmount - discounted.totalAmount)} (
                    {formatPercentage(discounted.discountPercentage, {
                        representedAs: 'fraction',
                    })}
                    )
                </p>
            </div>
            <div className={style['flex-space-between']}>
                <p>Total</p>
                <p>{Money.format(discounted.totalAmount)}</p>
            </div>
        </div>
    )
}
